import ResourcesIds from '../../resources/ResourcesIds';

/** What happened to the appointment. The server's `NotificationKind`, name for name. */
export type NotificationKind =
    | 'BOOKED'
    | 'CANCELLED'
    | 'MOVED'
    | 'CONFIRMED'
    | 'DECLINED'
    | 'EXPIRED'
    | 'WITHDRAWN';

/** Which side of the appointment the reader is on. */
export type NotificationAudience = 'PATIENT' | 'THERAPIST';

/**
 * One line of the bell, as facts: the words are chosen here, in the reader's language.
 *
 * `startsAt`/`endsAt` are the hours the event was about when it happened -- an appointment
 * since moved again still reads as it did -- and, like `createdAt`, are clinic wall-clock
 * with no zone.
 */
export interface INotification {
    id: number;
    kind: NotificationKind;
    audience: NotificationAudience;
    createdAt: string;
    read: boolean;
    appointmentId: number;
    startsAt: string;
    endsAt: string;
    /** Where it used to be. Only a move has one. */
    previousStartsAt?: string | null;
    previousEndsAt?: string | null;
    /** The therapist for a patient, the patient for a therapist. */
    otherPartyName?: string | null;
    otherPartyPictureUrl?: string | null;
    serviceNameRo?: string | null;
    serviceNameEn?: string | null;
    /** What the therapist wrote when declining, if anything. */
    decisionNote?: string | null;
}

export interface IUnreadCount {
    count: number;
}

/**
 * The headline for each kind, per side. Partial because not every kind reaches both sides --
 * nobody is told about an answer they gave themselves -- and a pairing the server never sends
 * falls back to a sentence that is at least true.
 */
const TITLES: Record<NotificationAudience, Partial<Record<NotificationKind, string>>> = {
    PATIENT: {
        BOOKED: ResourcesIds.NOTIFICATION_PATIENT_BOOKED,
        CANCELLED: ResourcesIds.NOTIFICATION_PATIENT_CANCELLED,
        MOVED: ResourcesIds.NOTIFICATION_PATIENT_MOVED,
        CONFIRMED: ResourcesIds.NOTIFICATION_PATIENT_CONFIRMED,
        DECLINED: ResourcesIds.NOTIFICATION_PATIENT_DECLINED,
        EXPIRED: ResourcesIds.NOTIFICATION_PATIENT_EXPIRED,
    },
    THERAPIST: {
        BOOKED: ResourcesIds.NOTIFICATION_THERAPIST_BOOKED,
        CANCELLED: ResourcesIds.NOTIFICATION_THERAPIST_CANCELLED,
        MOVED: ResourcesIds.NOTIFICATION_THERAPIST_MOVED,
        EXPIRED: ResourcesIds.NOTIFICATION_THERAPIST_EXPIRED,
        WITHDRAWN: ResourcesIds.NOTIFICATION_THERAPIST_WITHDRAWN,
    },
};

export const notificationTitleKey = (notification: INotification): string =>
    TITLES[notification.audience]?.[notification.kind] ?? ResourcesIds.NOTIFICATION_GENERIC;
